import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
  selector: 'input[separator]'
})
export class SeparatorDirective implements OnInit, OnDestroy {

  private sub: Subscription | null = null;

  constructor(private el: ElementRef<HTMLInputElement>, @Optional() @Self() private controlDir: NgControl) {}

  ngOnInit(): void {
    // Listen to programmatic changes from the FormControl (patchValue / setValue)
    if (this.controlDir && this.controlDir.control) {
      this.sub = this.controlDir.control.valueChanges?.subscribe(value => {
        // avoid formatting if input currently focused and user is typing (optional)
        // format and update DOM + control without re-emitting valueChanges
        const formatted = this.formatValue(value);
        if (formatted !== this.el.nativeElement.value) {
          this.el.nativeElement.value = formatted;
        }
        // update control value to the cleaned numeric value if you want control to keep plain number
        // or set to formatted string if you prefer
        // Use emitEvent: false to avoid loops
        try {
          this.controlDir.control.setValue(formatted, { emitEvent: false, onlySelf: true });
        } catch (e) {
          // ignore if control is disabled or cannot be set
        }
      });
    } else {
      // no NgControl available - nothing to subscribe to
    }

    // Also make sure initial native value is formatted (in case control had initial value)
    const initVal = this.controlDir?.control?.value ?? this.el.nativeElement.value;
    const initFormatted = this.formatValue(initVal);
    if (initFormatted !== this.el.nativeElement.value) {
      this.el.nativeElement.value = initFormatted;
      try {
        this.controlDir?.control?.setValue(initFormatted, { emitEvent: false, onlySelf: true });
      } catch (e) {}
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @HostListener('input')
  onInput() {
    const raw = this.el.nativeElement.value;
    const formatted = this.formatValue(raw);
    // update DOM only (user typing) — don't re-emit valueChanges here, the form control will update itself
    this.el.nativeElement.value = formatted;
  }

  @HostListener('blur')
  onBlur() {
    // ensure final formatting on blur
    const raw = this.el.nativeElement.value;
    const formatted = this.formatValue(raw);
    this.el.nativeElement.value = formatted;
    // sync control value (but don't emit)
    try {
      this.controlDir.control.setValue(formatted, { emitEvent: false, onlySelf: true });
    } catch (e) {}
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) return '';
    // if it's an object (form control value might be number), convert to string
    let str = String(value).trim();

    if (str === '' || str === '-') return '';

    // remove commas and non-numeric except dot and minus
    str = str.replace(/,/g, '').replace(/[^\d.-]/g, '');

    // handle multiple dots: keep first
    const parts = str.split('.');
    let intPart = parts[0] || '0';
    const decPart = parts.length > 1 ? parts.slice(1).join('') : undefined;

    // remove leading zeros on intPart but keep single zero if empty
    const negative = intPart.startsWith('-');
    if (negative) intPart = intPart.substring(1);
    intPart = intPart.replace(/^0+(?=\d)/, '') || '0';
    if (negative) intPart = '-' + intPart;

    // parse int part safely
    const intNumber = parseInt(intPart.replace(/[^0-9-]/g, ''), 10);
    if (Number.isNaN(intNumber)) return '';

    let formattedInt = Math.abs(intNumber).toLocaleString('en-US');
    if (intNumber < 0) formattedInt = '-' + formattedInt;

    if (decPart !== undefined) {
      // trim non-digits and limit decimal length if you want
      const cleanDec = decPart.replace(/\D/g, '');
      return formattedInt + (cleanDec.length ? '.' + cleanDec : '');
    }

    return formattedInt;
  }
}
